import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { motion } from "framer-motion";
import * as Dialog from "@radix-ui/react-dialog";

/** @type {Array<{id: string, label: string, storageKey: string, maxGuesses: number}>} */
const MODES = [
  {
    id: "character_stats",
    label: "Character Mode",
    storageKey: "kisekidle-chars",
    maxGuesses: 10,
  },
  {
    id: "quote_stats",
    label: "Quote Mode",
    storageKey: "kisekidle-quotes",
    maxGuesses: 6,
  },
  {
    id: "music_stats",
    label: "Music Mode",
    storageKey: "kisekidle-music",
    maxGuesses: 6,
  },
  {
    id: "location_stats",
    label: "Location Mode",
    storageKey: "kisekidle-locations",
    maxGuesses: 5,
  },
  {
    id: "trivia_stats",
    label: "Trivia Mode",
    storageKey: "kisekidle-trivia",
    maxGuesses: 2,
  },
  {
    id: "crafts_stats",
    label: "Crafts Mode",
    storageKey: "kisekidle-crafts",
    maxGuesses: 6,
  },
  {
    id: "silhouette_stats",
    label: "Silhouette Mode",
    storageKey: "kisekidle-silhouette",
    maxGuesses: 5,
  },
];

/**
 * Radix dialog fetching and displaying daily global community stats from Firestore.
 * Shows per-mode player count, win rate, average attempts, and top 5 guesses
 * (only revealed after the player has finished that day's puzzle).
 *
 * @param {{ targetDateStr: string, onClose: () => void, currentTheme: string }} props
 * @param {string} props.targetDateStr - Date string in "YYYY-M-D" format to fetch stats for.
 * @param {() => void} props.onClose - Callback to dismiss the dialog.
 * @param {string} props.currentTheme - Active era theme name for styling.
 * @returns {JSX.Element} The global stats modal.
 */
export default function GlobalStatsModal({ targetDateStr, onClose, currentTheme }) {
  const [statsData, setStatsData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchAllStats = async () => {
      setIsLoading(true);
      const fetchedStats = {};
      try {
        for (const mode of MODES) {
          const docRef = doc(db, mode.id, targetDateStr);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && isMounted) {
            fetchedStats[mode.id] = docSnap.data();
          }
        }
        if (isMounted) {
          setStatsData(fetchedStats);
        }
      } catch (error) {
        console.error("Error fetching global stats:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAllStats();
    return () => { isMounted = false; };
  }, [targetDateStr]);

  /** Checks localStorage to see if the player finished today's puzzle for a given mode. */
  const checkIfFinished = (storageKey, maxGuessesForMode) => {
    try {
      const savedData = localStorage.getItem(`${storageKey}-${targetDateStr}`);
      if (!savedData) return false;
      
      let parsedData;
      try {
        parsedData = JSON.parse(savedData);
      } catch (e) {
        return false;
      }

      let guesses = [];
      if (Array.isArray(parsedData)) {
        guesses = parsedData;
      } else if (parsedData && Array.isArray(parsedData.guesses)) {
        guesses = parsedData.guesses;
      }

      const hasWon = 
        parsedData.status === "won" || 
        parsedData.hasWon === true || 
        parsedData.gameState === "won";

      if (hasWon) return true;
      if (guesses.length >= maxGuessesForMode) return true;

      return false;
    } catch (e) {
      console.error("Anti-cheat check failed:", e);
      return false;
    }
  };

  /** Sorts and returns the top 5 most guessed answers from a tally object. */
  const getTop5 = (guessTally) => {
    if (!guessTally) return [];
    return Object.entries(guessTally)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  };

  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay forceMount asChild>
          <motion.div 
            className={`modal-overlay theme-${currentTheme}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        </Dialog.Overlay>
        <div className={`modal-content-wrapper theme-${currentTheme}`}>
          <Dialog.Content forceMount asChild>
            <motion.div
              className="modal-content global-stats-box"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <Dialog.Title className="sr-only">Global Daily Stats</Dialog.Title>
              <Dialog.Description className="sr-only">
                Statistics and most common guesses from the Kisekidle community for {targetDateStr}.
              </Dialog.Description>

              <Dialog.Close asChild>
                <button className="modal-close">
                  ✖
                </button>
              </Dialog.Close>

              <h2 className="mode-title" style={{ textAlign: "center", marginBottom: "20px" }}>
                🌍 Global Daily Stats
              </h2>

              {isLoading ? (
                <p style={{ textAlign: "center" }}>Loading global data...</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  {MODES.map((mode) => {
                    const data = statsData[mode.id] || {
                      totalPlays: 0,
                      totalWins: 0,
                      totalWinningAttempts: 0,
                      guessTally: {},
                    };

                    const isFinished = checkIfFinished(
                      mode.storageKey,
                      mode.maxGuesses
                    );

                    const winRate =
                      data.totalPlays > 0
                        ? Math.round((data.totalWins / data.totalPlays) * 100)
                        : 0;
                    const avgAttempts =
                      data.totalWins > 0
                        ? (data.totalWinningAttempts / data.totalWins).toFixed(1)
                        : 0;

                    const top5 = getTop5(data.guessTally);

                    return (
                      <div key={mode.id} className="stat-box" style={{ textAlign: "left" }}>
                        <h3 style={{ margin: "0 0 10px 0" }}>
                          {mode.label}
                        </h3>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                            fontSize: "0.9rem",
                          }}
                        >
                          <span>
                            <strong>Players:</strong> {data.totalPlays}
                          </span>
                          <span>
                            <strong>Win Rate:</strong> {winRate}%
                          </span>
                          <span>
                            <strong>Avg Attempts:</strong> {avgAttempts}
                          </span>
                        </div>

                        <div
                          style={{
                            fontSize: "0.9rem",
                            color: "inherit",
                            marginTop: "10px",
                          }}
                        >
                          <strong>Most Common Guesses:</strong>

                          {isFinished ? (
                            top5.length > 0 ? (
                              <ul style={{ margin: "10px 0 0 0", padding: 0 }}>
                                {top5.map(([name, count]) => {
                                  const percent =
                                    data.totalPlays > 0
                                      ? Math.round((count / data.totalPlays) * 100)
                                      : 0;

                                  const barColor =
                                    percent >= 70
                                      ? "#4CAF50"
                                      : percent >= 50
                                      ? "#FF9800"
                                      : "#4a90e2";

                                  return (
                                    <li
                                      key={name}
                                      style={{
                                        position: "relative",
                                        marginBottom: "8px",
                                        padding: "6px 12px",
                                        borderRadius: "6px",
                                        backgroundColor: "var(--stat-bar-bg, rgba(0,0,0,0.2))",
                                        listStyleType: "none",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        zIndex: 1,
                                        overflow: "hidden",
                                        border: "var(--stat-bar-border, 1px solid rgba(255,255,255,0.1))"
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          height: "100%",
                                          width: `${percent}%`,
                                          backgroundColor: barColor,
                                          opacity: 0.3,
                                          zIndex: -1,
                                          transition: "width 0.5s ease-out",
                                        }}
                                      />

                                      <span
                                        style={{
                                          fontWeight: "bold",
                                          zIndex: 2,
                                        }}
                                      >
                                        {name}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "0.85rem",
                                          zIndex: 2,
                                          opacity: 0.8
                                        }}
                                      >
                                        {count}{" "}
                                        <span style={{ opacity: 0.7 }}>
                                          ({percent}%)
                                        </span>
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <div
                                style={{
                                  marginTop: "5px",
                                  opacity: 0.6,
                                  fontStyle: "italic",
                                }}
                              >
                                No specific guesses recorded yet!
                              </div>
                            )
                          ) : (
                            <div
                              style={{
                                marginTop: "5px",
                                padding: "10px",
                                backgroundColor: "rgba(0,0,0,0.1)",
                                borderRadius: "6px",
                                textAlign: "center",
                                opacity: 0.6,
                                fontStyle: "italic",
                              }}
                            >
                              🔒 Play today's {mode.label} to unlock these answers!
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
